import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Chip, Icon, Table, TTableColumn, Tooltip } from '@treezor/design-system';

import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';

import { WebhookAction } from '@/features/webhooks/components/WebhookAction';
import { openModal } from '@/store/actions/core.actions';
import { searchWebhooks } from '@/features/webhooks/store/webhook.actions';
import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { TableSkeleton } from '@/ui-kit/loaders/TableSkeleton';
import { Page } from '@/ui-kit/templates/Page';

const tableColumns = ['status', 'url', 'event', 'actions'] as const;

type TWebhooksCell = Record<(typeof tableColumns)[number], ReactNode>;

export const Webhooks = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const webhooks = useAppSelector((state) => state.webhook.webhooks);
  const isLoading = useAppSelector((state) => state.webhook.isLoading);

  useEffect(() => {
    dispatch(searchWebhooks());
    if (window.location.search === '?error') {
      // @ts-expect-error: Test trigger AWS RUM Error
      undefinedVariable;
    }
  }, [dispatch]);

  const headers: TTableColumn<TWebhooksCell>[] = tableColumns.map((field) => ({
    name: field,
    field,
    label: t(`pages.administration.webhooks.table.${field}`),
  }));

  const rows = webhooks?.map((webhook) => ({
    status:
      webhook.status === 'ENABLED' ? (
        <Tooltip messageContent={t('pages.administration.webhooks.table.enabled') as string}>
          <Icon name="check_circle" className="text-icon-success" />
        </Tooltip>
      ) : (
        <Tooltip messageContent={t('pages.administration.webhooks.table.pending') as string}>
          <Icon name="pause" className="text-icon-tertiary" />
        </Tooltip>
      ),
    url: (
      <Tooltip messageContent={t('pages.administration.webhooks.table.id', { id: webhook.uuid }) as string}>
        <span className="typo-body-regular-small text-secondary">{webhook.url}</span>
      </Tooltip>
    ),
    event: (
      <div className="flex flex-wrap gap-2">
        {webhook.events?.map((event, index) => (
          <Chip key={index} label={event} size="s" />
        ))}
      </div>
    ),
    actions: <WebhookAction webhook={webhook} />,
  }));

  const toggleAddWebhookModal = () => dispatch(openModal('addWebhookModal'));

  return (
    <Page
      actions={[
        {
          id: 'add-webhook-btn',
          label: t('pages.administration.webhooks.addWebhookBtn'),
          icon: 'add',
          onClick: toggleAddWebhookModal,
        },
      ]}
    >
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <Table
          columns={headers}
          rows={rows}
          className="bg-primary"
          emptyComponent={
            <EmptyState title={t('components.molecules.table.noRecord')} icon="search_v1" size="m" className="p-6" />
          }
        />
      )}
    </Page>
  );
};
